import React from 'react';
import {
    VStack,
    FormControl,
    FormLabel,
    Input,
    Select,
    Button,
    Text,
    Box,
    Divider,
    HStack,
    Icon,
    RadioGroup,
    Radio,
} from '@chakra-ui/react';
import { MdCheckCircle, MdCancel, MdWarning, MdMailOutline, MdMarkEmailRead, MdPhone } from 'react-icons/md';

export default function MessageReadProperties({
    nodeConfig,
    setNodeConfig,
    updateNodeData,
    setSelectedNode
}) {
    const trackingMode = nodeConfig.trackingMode || 'read';

    const trackingLabels = {
        read: { receipt: 'Read Receipt', action: 'Message Read', notAction: 'Not Read' },
        delivered: { receipt: 'Delivery Receipt', action: 'Message Delivered', notAction: 'Not Delivered' },
        answered: { receipt: 'Call Answer', action: 'Call Answered', notAction: 'Not Answered' },
    };

    const current = trackingLabels[trackingMode] || trackingLabels.read;

    return (
        <VStack spacing={4} align="stretch">
            <FormControl>
                <FormLabel fontSize="sm">Step Name</FormLabel>
                <Input
                    size="sm"
                    value={nodeConfig.label || ''}
                    onChange={(e) => setNodeConfig({ ...nodeConfig, label: e.target.value })}
                    placeholder="e.g., Check Message Read"
                />
            </FormControl>
            
            <Divider />

            {/* Tracking Mode Selection */}
            <FormControl>
                <FormLabel fontSize="sm" mb={3}>Tracking Mode</FormLabel>
                <RadioGroup
                    value={trackingMode}
                    onChange={(value) => setNodeConfig({ ...nodeConfig, trackingMode: value })}
                >
                    <VStack spacing={2} align="stretch">
                        <Box 
                            p={3} 
                            borderWidth="1px" 
                            borderRadius="lg" 
                            cursor="pointer"
                            bg={trackingMode === 'read' ? 'blue.50' : 'white'}
                            borderColor={trackingMode === 'read' ? 'blue.300' : 'gray.200'}
                            _hover={{ bg: trackingMode === 'read' ? 'blue.50' : 'gray.50' }}
                            onClick={() => setNodeConfig({ ...nodeConfig, trackingMode: 'read' })}
                        >
                            <HStack spacing={2}>
                                <Radio value="read" />
                                <VStack align="flex-start" spacing={0} flex={1}>
                                    <HStack>
                                        <Icon as={MdMarkEmailRead} color="#155dfc" />
                                        <Text fontSize="sm" fontWeight="medium">Message Read</Text>
                                    </HStack>
                                    <Text fontSize="xs" color="gray.600">
                                        Track when customer reads the message (max 300 chars)
                                    </Text>
                                </VStack>
                            </HStack>
                        </Box>

                        <Box 
                            p={3} 
                            borderWidth="1px" 
                            borderRadius="lg" 
                            cursor="pointer"
                            bg={trackingMode === 'delivered' ? 'green.50' : 'white'}
                            borderColor={trackingMode === 'delivered' ? 'green.300' : 'gray.200'}
                            _hover={{ bg: trackingMode === 'delivered' ? 'green.50' : 'gray.50' }}
                            onClick={() => setNodeConfig({ ...nodeConfig, trackingMode: 'delivered' })}
                        >
                            <HStack spacing={2}>
                                <Radio value="delivered" />
                                <VStack align="flex-start" spacing={0} flex={1}>
                                    <HStack>
                                        <Icon as={MdMailOutline} color="#00c951" />
                                        <Text fontSize="sm" fontWeight="medium">Message Delivered</Text>
                                    </HStack>
                                    <Text fontSize="xs" color="gray.600">
                                        Track when message is delivered to customer (max 200 chars)
                                    </Text>
                                </VStack>
                            </HStack>
                        </Box>

                        <Box 
                            p={3} 
                            borderWidth="1px" 
                            borderRadius="lg" 
                            cursor="pointer"
                            bg={trackingMode === 'answered' ? 'purple.50' : 'white'}
                            borderColor={trackingMode === 'answered' ? 'purple.300' : 'gray.200'}
                            _hover={{ bg: trackingMode === 'answered' ? 'purple.50' : 'gray.50' }}
                            onClick={() => setNodeConfig({ ...nodeConfig, trackingMode: 'answered' })}
                        >
                            <HStack spacing={2}>
                                <Radio value="answered" />
                                <VStack align="flex-start" spacing={0} flex={1}>
                                    <HStack>
                                        <Icon as={MdPhone} color="#9333ea" />
                                        <Text fontSize="sm" fontWeight="medium">Call Answered</Text>
                                    </HStack>
                                    <Text fontSize="xs" color="gray.600">
                                        Track when customer answers the call
                                    </Text>
                                </VStack>
                            </HStack>
                        </Box>
                    </VStack>
                </RadioGroup>
            </FormControl>

            <Divider />

            <VStack spacing={4} align="stretch">
                <FormControl>
                    <FormLabel fontSize="sm" mb={3}>
                        Wait Time for {current.receipt}
                    </FormLabel>
                    <HStack>
                        <Input
                            size="sm"
                            type="number"
                            min={1}
                            value={nodeConfig.waitTime}
                            onChange={(e) => setNodeConfig({ ...nodeConfig, waitTime: parseInt(e.target.value) })}
                            placeholder="24"
                        />
                        <Select
                            size="sm"
                            value={nodeConfig.waitUnit || 'hours'}
                            onChange={(e) => setNodeConfig({ ...nodeConfig, waitUnit: e.target.value })}
                            w="140px"
                        >
                            <option value="minutes">Minutes</option>
                            <option value="hours">Hours</option>
                            <option value="days">Days</option>
                        </Select>
                    </HStack>
                    <Text fontSize="xs" color="gray.600" mt={2}>
                        How long to wait for {trackingMode === 'answered' ? 'the call to be answered' : `a ${current.receipt.toLowerCase()}`} before routing to "No" path
                    </Text>
                </FormControl>

                <Divider />

                <Box>
                    <FormLabel fontSize="sm" mb={3}>Route Configuration</FormLabel>

                    <VStack spacing={3} align="stretch">
                        <Box p={3} bg="green.50" borderWidth="1px" borderColor="green.200" borderRadius="lg">
                            <HStack mb={2}>
                                <Icon as={MdCheckCircle} color="green.600" />
                                <Text fontSize="sm" fontWeight="semibold" color="green.900">
                                    Yes Route ({current.action})
                                </Text>
                            </HStack>
                            <Text fontSize="xs" color="green.800">
                                {trackingMode === 'delivered' 
                                    ? 'Message was successfully delivered to the customer within the wait time. Connect this to the next action.'
                                    : trackingMode === 'answered'
                                    ? 'Customer answered the call within the wait time. Connect this to the next action for engaged customers.'
                                    : 'Customer has read the message within the wait time. Connect this to the next action for engaged customers.'}
                            </Text>
                        </Box>

                        <Box p={3} bg="red.50" borderWidth="1px" borderColor="red.200" borderRadius="lg">
                            <HStack mb={2}>
                                <Icon as={MdCancel} color="red.600" />
                                <Text fontSize="sm" fontWeight="semibold" color="red.900">
                                    No Route ({current.notAction})
                                </Text>
                            </HStack>
                            <Text fontSize="xs" color="red.800">
                                {trackingMode === 'delivered'
                                    ? 'Message was not delivered after the wait time. Connect this to send via alternative channel or retry.'
                                    : trackingMode === 'answered'
                                    ? 'Customer did not answer the call after the wait time. Connect this to retry, leave a voicemail, or try an alternative channel.'
                                    : 'Customer has not read the message after the wait time. Connect this to send a reminder or follow-up action.'}
                            </Text>
                        </Box>
                    </VStack>
                </Box>

                <Box p={3} bg="orange.50" borderWidth="1px" borderColor="orange.200" borderRadius="lg">
                    <HStack align="start" spacing={2}>
                        <Icon as={MdWarning} color="orange.600" mt={0.5} flexShrink={0} />
                        <VStack align="start" spacing={1} fontSize="xs" color="orange.800">
                            <Text fontWeight="medium">
                                How {current.action} Works
                            </Text>
                            <Text>
                                {trackingMode === 'delivered'
                                    ? 'The system waits for the specified duration for a delivery receipt. If received, it routes to "Yes", otherwise to "No". This helps you handle delivery failures and retry logic.'
                                    : trackingMode === 'answered'
                                    ? 'The system waits for the specified duration for the call to be answered. If answered, it routes to "Yes", otherwise to "No". This helps you create call-based follow-up workflows.'
                                    : 'The system waits for the specified duration for a read receipt. If received, it routes to "Yes", otherwise to "No". This helps you create engagement-based follow-ups.'}
                            </Text>
                        </VStack>
                    </HStack>
                </Box>
            </VStack>

            <Divider />

            <HStack>
                <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setSelectedNode && setSelectedNode(null)}
                >
                    Cancel
                </Button>
                <Button
                    size="sm"
                    colorScheme="blue"
                    bg="var(--brand-primary)"
                    _hover={{ bg: 'var(--brand-primary-hover)' }}
                    onClick={updateNodeData}
                    flex={1}
                >
                    Update Node
                </Button>
            </HStack>
        </VStack>
    );
}